'use client';

import { track } from '@/utils/rudderstack';
import { isSessionStorageAvailable } from '@/utils/environment';

declare global {
  interface Window {
    msaiData?: {
      getAllUserBucketedExperiments: () => MoonshotExperiment[];
    };
  }
}

export interface MoonshotExperiment {
  ExperimentId: number;
  ExperimentName: string;
  VariantId: number;
  VariantName: string;
}

function _setExperimentsJson(experimentsJson: string) {
  try {
    sessionStorage.setItem('msai_experiments', experimentsJson);
    return true;
  } catch (e) {
    console.error('_setExperimentsJson : Error storing experiments', e);
    return false;
  }
}

function _getExperimentsJson() {
  try {
    return sessionStorage.getItem('msai_experiments');
  } catch (e) {
    console.error('_getExperimentsJson : Error retrieving experiments', e);
    return null;
  }
}

type ExperimentEntry = {
  experiment_id: number;
  experiment_name: string;
  variant_id: number;
  variant_name: string;
};

function _formatExperiments(
  experiments: MoonshotExperiment[],
): ExperimentEntry[] {
  return experiments.map((experiment) => ({
    experiment_id: experiment.ExperimentId,
    experiment_name: experiment.ExperimentName,
    variant_id: experiment.VariantId,
    variant_name: experiment.VariantName,
  }));
}

function _trackExperiments(experiments: MoonshotExperiment[]) {
  try {
    track('Moonshot AI Experiments Viewed', {
      msai_experiments: _formatExperiments(experiments),
    });
  } catch (e) {
    console.error('_trackExperiments : Error tracking experiments', e);
  }
}

export function processMoonshotExperiments() {
  if (
    !window.msaiData ||
    typeof window.msaiData.getAllUserBucketedExperiments !== 'function'
  ) {
    return;
  }

  let experiments: MoonshotExperiment[];
  try {
    experiments = window.msaiData.getAllUserBucketedExperiments();
  } catch (e) {
    console.error('processMoonshotExperiments : Error getting experiments', e);
    return;
  }

  if (!experiments || experiments.length === 0) {
    return;
  }

  if (!isSessionStorageAvailable()) {
    return;
  }

  const newExperimentsJson = JSON.stringify(experiments);
  const oldExperimentsJson = _getExperimentsJson();

  if (newExperimentsJson === oldExperimentsJson) {
    return;
  }

  _setExperimentsJson(newExperimentsJson);
  _trackExperiments(experiments);
}
